import { AnalyticsTransformer } from '@cue/utility';
import { useEffect, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { v4 as uuidv4 } from 'uuid';

class InitialQueue {
  private queue: useShowroomTrackingQueue[] = [];
  private static _instance: InitialQueue = new InitialQueue();
  private inited = false;
  private debugEnabled = false;
  private currentScene: enterSceneProps | undefined = undefined;
  private currentTour: enterTourProps | undefined = undefined;

  constructor() {
    if (InitialQueue._instance) {
      return InitialQueue._instance;
    }
    this.inited = false;
    this.queue = [];
  }

  getQueue() {
    return this.queue;
  }

  addToQueue(tracking: useShowroomTrackingQueue) {
    if (tracking.action === 'startSession') {
      if (this.inited) return;
      this.inited = true;
    }
    this.queue.push(tracking);
  }
  setDebugEnabled() {
    this.debugEnabled = true;
  }
  getDebugEnabled() {
    return this.debugEnabled;
  }
  setCurrentScene(scene: enterSceneProps) {
    this.currentScene = { ...scene, duration: Date.now() };
  }
  getCurrentScene(): useShowroomTrackingDataBase | undefined {
    return this.currentScene;
  }

  setCurrentTour(tour: enterTourProps) {
    this.currentTour = tour;
  }
  getCurrentTour(): enterTourProps | undefined {
    return this.currentTour;
  }

  isInited() {
    return this.inited;
  }
}
const initialQueue = new InitialQueue();

const defaultState = {
  debuggingEnabled: () => {
    initialQueue.setDebugEnabled();
  },
  startSession: (props: startSessionProps) => {
    initialQueue.setCurrentScene(props);
    initialQueue.addToQueue({ action: 'startSession', data: props });
  },
  enterScene: (props: enterSceneProps) => {
    initialQueue.setCurrentScene({ ...props, duration: Date.now() });
    initialQueue.addToQueue({
      action: 'enterScene',
      data: { ...props, duration: Date.now(), contentDuration: 0 },
    });
  },
  exitScene: () => {
    return;
  },
  enterTour: (props: enterTourProps, scene: enterSceneProps) => {
    const currentScene = initialQueue.getCurrentScene();
    initialQueue.setCurrentTour({
      ...props,
      duration: Date.now(),
      visited: [props.tourStep],
      visitedPercentage: 0,
    });
    const data: enterTourProps & Partial<useShowroomTrackingDataBase> = currentScene?.sceneId
      ? { ...currentScene, ...props }
      : { ...scene, ...props };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { totalSteps, ...rest } = data;
    initialQueue.addToQueue({
      action: 'enterTour',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore // TODO
      data: rest,
    });
  },

  pushTourStep: () => {
    return;
  },

  exitTour: () => {
    return;
  },
  openContent: () => {
    return;
  },
  closeContent: () => {
    return;
  },
};

export const useShowroomTrackingDeprecatedImpl = (): useShowroomTrackingDeprecatedReturn => {
  const TRACKING_PRE = 'experience';
  const [currentOpenContent, setCurrentOpenContent] = useState<openContentProps>();
  const [currentTour, setCurrentTour] = useState<enterTourProps>();
  const [sessionId] = useState<string>(uuidv4());
  const [debug, setDebug] = useState(initialQueue.getDebugEnabled());
  const [inited, setInited] = useState(initialQueue.isInited());
  const [currentScene, setCurrentScene] = useState<enterSceneProps>();
  const [currentSceneContentDuration, setCurrentSceneContentDuration] = useState<number>(0);
  useEffect(() => {
    initialQueue.getQueue().forEach(({ action, data }) => {
      if (action == 'enterScene') {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { duration, ...rest } = data;
        trackEvent(action, rest);
      } else {
        trackEvent(action, data);
      }
    });
    if (initialQueue.getDebugEnabled()) {
      setDebug(true);
    }
    if (initialQueue.getCurrentScene()) setCurrentScene(initialQueue.getCurrentScene());

    if (initialQueue.getCurrentTour()) setCurrentTour(initialQueue.getCurrentTour());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debuggingEnabled = () => setDebug(true);
  const startSession = (props: startSessionProps) => {
    setCurrentScene(props);
    if (!inited) trackEvent('startSession', { ...props });
    setInited(true);
  };

  const enterScene = (props: enterSceneProps) => {
    setCurrentScene({ ...props, duration: Date.now(), contentDuration: 0 });
    setCurrentSceneContentDuration(0);
    trackEvent('enterScene', props);
  };

  const exitScene = () => {
    if (currentScene) {
      const data: enterSceneProps = {
        ...currentScene,
        duration: (Date.now() - (currentScene?.duration || Date.now())) / 1000,
        contentDuration: currentSceneContentDuration / 1000,
      };

      trackEvent('exitScene', data);
    }
  };

  const enterTour = (props: enterTourProps, scene: enterSceneProps) => {
    if (currentTour?.tourId === props.tourId) return;
    setCurrentTour({
      ...props,
      duration: Date.now(),
      visited: [props.tourStep],
      visitedPercentage: 0,
    });
    const s = currentScene || scene;
    const data: enterTourProps & Partial<useShowroomTrackingDataBase> = {
      ...s,
      ...props,
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { totalSteps, ...rest } = data;
    trackEvent('enterTour', rest);
  };

  const exitTour = () => {
    if (!currentTour) return console.warn(' NO CURENT TOUR FOR exitTour()');
    const visited = [...new Set(currentTour?.visited)].sort((a, b) => a - b);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { totalSteps, ...dynamicTour } = currentTour;
    trackEvent('exitTour', {
      ...currentScene,
      ...dynamicTour,
      duration: (Date.now() - (currentTour?.duration || 0)) / 1000,
      visited,
      visitedPercentage: (visited?.length || 1) / (currentTour?.totalSteps || 0),
    });
    setCurrentTour(undefined);
  };

  const pushTourStep = (props: pushTourStepProps) => {
    if (currentTour && currentTour.tourId === props.tourId) {
      setCurrentTour({
        ...currentTour,
        visited: [...(currentTour?.visited || []), props.tourStep],
      });
    }
  };

  const openContent = (props: openContentProps) => {
    const data: openContentProps & Partial<useShowroomTrackingDataBase> = currentScene
      ? { ...currentScene, ...props }
      : props;

    setCurrentOpenContent({ ...props, duration: Date.now() });
    trackEvent('openContent', data);
  };

  const closeContent = () => {
    if (currentOpenContent) {
      const dur = Date.now() - (currentOpenContent?.duration || 0);
      trackEvent('closeContent', {
        ...currentScene,
        ...currentOpenContent,
        duration: dur / 1000,
      });
      setCurrentOpenContent(undefined);
      // UPDATE SCENE DURATION
      if (currentScene?.duration) {
        setCurrentScene({
          ...currentScene,
          //duration: currentScene?.duration + dur,
        });
      }
      // UPDATE TOUR DURATION
      if (currentTour?.duration) {
        setCurrentTour({
          ...currentTour,
          //duration: currentTour?.duration + dur,
        });
      }
      setCurrentSceneContentDuration((prev) => prev + dur);
    }
  };

  const trackEvent = <T extends Partial<useShowroomTrackingDataBase>>(
    action: useShowroomTrackingActions,
    data?: T,
  ) => {
    if (!data?.sceneId) return console.error('NO TRACKING PROPS FOR CURRENT DATA', action, data);

    AnalyticsTransformer.customEvent([
      TRACKING_PRE,
      `${action}||${JSON.stringify({
        sessionId,
        ...data,
      })}`,
    ]);

    if (debug) {
      console.log(
        `%c  TRACKING ${action}||${JSON.stringify(
          {
            sessionId,
            ...data,
          },
          null,
          2,
        )}`,
        'background: #fff; color: #900',
      );
    }
  };

  return {
    startSession,
    enterScene,
    exitScene,
    openContent,
    closeContent,
    enterTour,
    exitTour,
    pushTourStep,
    debuggingEnabled,
  };
};

export const useShowroomTrackingDeprecated = singletonHook<useShowroomTrackingDeprecatedReturn>(
  defaultState,
  useShowroomTrackingDeprecatedImpl,
);

interface useShowroomTrackingDataBase {
  sceneId: string;
  sceneName: string;
  position: string;
}

type useShowroomTrackingActions = keyof useShowroomTrackingDeprecatedReturn;

export interface useShowroomTrackingDeprecatedReturn {
  startSession: (props: startSessionProps) => void;
  enterScene: (props: enterSceneProps) => void;
  exitScene: () => void;
  enterTour: (props: enterTourProps, scene: enterSceneProps) => void;
  exitTour: () => void;
  pushTourStep: (props: pushTourStepProps) => void;
  openContent: (props: openContentProps) => void;
  closeContent: () => void;
  debuggingEnabled: () => void;
}

type startSessionProps = useShowroomTrackingDataBase;
type enterSceneProps = useShowroomTrackingDataBase & {
  duration?: number;
  contentDuration?: number;
};
type enterTourProps = {
  tourName: string;
  tourId: string;
  tourStep: number;
  totalSteps?: number;
  duration?: number;
  visited?: number[];
  visitedPercentage?: number;
};
type pushTourStepProps = {
  tourId: string;
  tourStep: number;
};
type openContentProps = {
  contentId: string;
  position: string;
  title: string;
  duration?: number;
};

type useShowroomTrackingQueue = {
  action: useShowroomTrackingActions;
  data: useShowroomTrackingDataBase & Partial<enterSceneProps>;
};

/*
Possible Tracking states
- startSession ✅
  {sessionId, sceneId, position, sceneName }

- enterScene ✅
  {sessionId, sceneId, position, sceneName }

- exitScene ✅
  {sessionId, sceneId, position, sceneName }

- openContent ✅
  {sessionId, sceneId, sceneName, contentId, position, title }

- closeContent ✅
  {sessionId, sceneId, position, sceneName, contentId, position, title, duration }

- openLink
  {sessionId, sceneId, position, sceneName, url }

- enterTour ✅
  {sessionId, sceneId, position, sceneName, tourName, tourId, tourStep }

- exitTour
  {sessionId, sceneId, position, sceneName, tourName, tourId, tourStep, duration, visited=[...tourSteps], visitedPercentage  }

// HELPER FUNCTIONS
- pushTourStep
  {tourId, tourStep}
TODO Fairtouch

*/
